import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { foods } from "@/mocks/foods";
import { searchServices } from "@/mocks/site";

const navItems = [
  { label: "首页", to: "/" },
  { label: "美食分类", to: "/food-category" },
  { label: "快递服务", to: "/courier" },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

interface FoodHit {
  id: string;
  name: string;
  category: string;
  price: number;
}

export default function Navbar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const keyword = query.trim();
  const foodHits: FoodHit[] = keyword
    ? foods
        .filter((f) => f.name.includes(keyword) || f.category.includes(keyword) || f.tags.some((t) => t.includes(keyword)))
        .slice(0, 5)
        .map((f) => ({ id: f.id, name: f.name, category: f.category, price: f.price }))
    : [];
  const serviceHits = keyword ? searchServices.filter((s) => s.name.includes(keyword) || s.desc.includes(keyword)) : [];
  const hasResult = foodHits.length > 0 || serviceHits.length > 0;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const renderHighlight = (text: string) => {
    if (!keyword) return text;
    const idx = text.indexOf(keyword);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="text-primary-600 font-bold">{keyword}</span>
        {text.slice(idx + keyword.length)}
      </>
    );
  };

  const goFood = (id: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/food-detail/${id}`);
  };

  const goService = (to: string) => {
    setOpen(false);
    setQuery("");
    navigate(to);
  };

  const searchPanel = (
    <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-2xl border border-background-200 bg-background-50 p-3 shadow-lg animate-slide-in">
      {!keyword && (
        <p className="px-2 py-3 text-sm text-foreground-500">试试搜索「牛肉面」「甜点」或「快递查询」</p>
      )}
      {keyword && !hasResult && (
        <p className="px-2 py-3 text-sm text-foreground-500">
          没有找到「{keyword}」相关的美食或服务，换个关键词试试吧
        </p>
      )}
      {keyword && foodHits.length > 0 && (
        <div className="mb-2">
          <p className="px-2 pb-1 text-xs font-bold uppercase tracking-wider text-foreground-400">美食</p>
          <ul>
            {foodHits.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => goFood(f.id)}
                  className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left transition hover:bg-primary-50"
                >
                  <span className="text-sm text-foreground-900">{renderHighlight(f.name)}</span>
                  <span className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">{f.category}</span>
                    <span className="font-bold text-primary-600">¥{f.price}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {keyword && serviceHits.length > 0 && (
        <div>
          <p className="px-2 pb-1 text-xs font-bold uppercase tracking-wider text-foreground-400">快递服务</p>
          <ul>
            {serviceHits.map((s) => (
              <li key={s.name}>
                <button
                  type="button"
                  onClick={() => goService(s.to)}
                  className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left transition hover:bg-accent-50"
                >
                  <span className="text-sm text-foreground-900">{renderHighlight(s.name)}</span>
                  <span className="text-xs text-foreground-500">{s.desc}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden bg-primary-950 text-primary-100 md:block">
        <div className="flex w-full items-center justify-between px-4 py-2 text-xs md:px-8">
          <p className="flex items-center gap-2">
            <i className="ri-gift-2-line" />
            新客首单立减 15 元 · 满 68 元免配送费 · 周三会员日全场 8 折
          </p>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5">
              <i className="ri-phone-line" />
              400-880-1234
            </span>
            <Link to="/courier-track" className="transition hover:text-background-50">
              查询快递
            </Link>
          </div>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex w-full items-center gap-4 px-4 py-3 md:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50">
              <i className="ri-e-bike-2-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">美味的快递</span>
              <span className="hidden text-[11px] tracking-wide text-foreground-500 sm:block">美食外卖 · 同城急送</span>
            </span>
          </Link>

          <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto hidden items-center gap-3 lg:flex">
            <div ref={boxRef} className="relative w-64">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2 transition focus-within:border-primary-400">
                <i className="ri-search-line text-foreground-400 text-base" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                  }}
                  onFocus={() => setOpen(true)}
                  placeholder="搜索美食或快递服务"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              {open && searchPanel}
            </div>
            <Link
              to="/food-category"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-shopping-bag-3-line" />
              立即订餐
            </Link>
          </div>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => setMobileSearch((v) => !v)}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-full border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className="ri-search-line text-lg" />
          </button>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
          </button>
        </div>

        {mobileSearch && (
          <div className="border-t border-background-200 px-4 py-3 lg:hidden">
            <div className="relative">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 py-2">
                <i className="ri-search-line text-foreground-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                  }}
                  onFocus={() => setOpen(true)}
                  placeholder="搜索美食或快递服务"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                />
              </div>
              {open && searchPanel}
            </div>
          </div>
        )}

        {menuOpen && (
          <nav className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
            <ul className="flex flex-col gap-1">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition ${
                        isActive ? "bg-primary-100 text-primary-700" : "text-foreground-800 hover:bg-background-100"
                      }`
                    }
                  >
                    <i className="ri-arrow-right-s-line" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="mt-1 flex gap-2 px-1">
                <Link
                  to="/food-category"
                  onClick={() => setMenuOpen(false)}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-primary-500 px-4 py-3 text-sm font-bold text-background-50"
                >
                  <i className="ri-shopping-bag-3-line" />
                  立即订餐
                </Link>
                <Link
                  to="/courier-send"
                  onClick={() => setMenuOpen(false)}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-accent-500 px-4 py-3 text-sm font-bold text-background-50"
                >
                  <i className="ri-send-plane-fill" />
                  立即寄件
                </Link>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
}